import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import LoadingScreen from "@components/LoadingInterstitial";

import { hasTechniquesNamespace, loadTechniquesNamespace } from "@/i18n";

/**
 * True once techniques.json is loaded; starts the load if it is not. Also for
 * components that render outside the gated member tree but read a
 * techniques-only key (the member menu on /gifts, for instance): mounting
 * with the namespace missing shows the key's defaultValue, and the state
 * flip here is what re-renders them with the real copy once it lands, since
 * react-i18next only rebinds on languageChanged.
 */
export const useTechniquesNamespace = (): boolean => {
  const [ready, setReady] = useState(hasTechniquesNamespace);

  useEffect(() => {
    if (ready) return;
    let active = true;
    void loadTechniquesNamespace().then(() => {
      if (active) setReady(true);
    });
    return () => {
      active = false;
    };
  }, [ready]);

  return ready;
};

/**
 * Holds the member route tree until `techniques.json` is loaded (OMG-1836).
 *
 * The namespace left the i18n init list so that /login, /gifts, /redeem and
 * the pre-mount part of every member load stop waiting on its ~180 KB. Member
 * pages read it through `fallbackNS`, and a fallback lookup neither triggers
 * a load nor re-renders when one lands later (react-i18next only rebinds on
 * `languageChanged`), so without this gate a page that mounted first would
 * show raw key names and keep them.
 *
 * Deliberately a plain loading state, not Suspense: the member tree already
 * shows LoadingScreen for the auth check in the same spot, and an outer
 * Suspense fallback here would be the full-screen Fallback flash main.tsx
 * went to lengths to avoid. loadTechniquesNamespace always resolves (timeout
 * or failure degrade to raw keys, as a failed init already does), so the
 * loader can never stay up.
 */
export const RequireTechniquesNamespace = ({
  children,
}: {
  children: ReactNode;
}) => {
  const ready = useTechniquesNamespace();
  if (!ready) return <LoadingScreen />;
  return <>{children}</>;
};
